import { useRef, useState } from 'react';
import { api } from '@/api';
import { cn } from '@/lib/classNames';
import type { LenderSuggestion } from '@/types/api';

interface LenderAutocompleteProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** Called with the full suggestion when one is picked (e.g. to fill address). */
  onSelect?: (suggestion: LenderSuggestion) => void;
}

/**
 * Lender-name typeahead. Mirrors the live app's `.lenderauto` jQuery-UI
 * autocomplete: debounced prefix search of the Lenders table (min 2 chars),
 * a name + "City, ST" suggestion list, and click/keyboard select that fills the
 * name. Fetches only on user typing, so programmatic value changes (form
 * reset / OCR fill) never pop the dropdown.
 */
export function LenderAutocomplete({ id, value, onChange, onSelect }: LenderAutocompleteProps) {
  const [items, setItems] = useState<LenderSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const timer = useRef<number | undefined>(undefined);

  const handleType = (v: string) => {
    onChange(v);
    window.clearTimeout(timer.current);
    const term = v.trim();
    if (term.length < 2) {
      setItems([]);
      setOpen(false);
      return;
    }
    timer.current = window.setTimeout(async () => {
      try {
        const res = await api.getLenders(term);
        setItems(res);
        setOpen(res.length > 0);
        setHi(-1);
      } catch {
        setItems([]);
        setOpen(false);
      }
    }, 250);
  };

  const pick = (item: LenderSuggestion) => {
    onChange(item.label);
    onSelect?.(item);
    setItems([]);
    setOpen(false);
    setHi(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => Math.min(items.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter' && hi >= 0) {
      e.preventDefault();
      pick(items[hi]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="ast-autocomplete">
      <input
        id={id}
        className="form-control form-control-sm"
        autoComplete="off"
        value={value ?? ''}
        onChange={(e) => handleType(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => { if (items.length > 0) setOpen(true); }}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
      />
      {open && (
        <ul className="ast-ac-list" role="listbox">
          {items.map((item, idx) => (
            <li
              key={item.id}
              role="option"
              aria-selected={idx === hi}
              className={cn('ast-ac-item', idx === hi && 'active')}
              onMouseDown={(e) => { e.preventDefault(); pick(item); }}
              onMouseEnter={() => setHi(idx)}
            >
              <div className="ast-ac-name">{item.label}</div>
              {item.desc && <div className="ast-ac-desc">{item.desc}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
